/**
 * Workbook comparison (Layer 6 — parallel-run reconciliation, Task 66).
 *
 * Records cell-level platform-vs-legacy-workbook variances during the
 * parallel-run period and shows the BE's ±0.01% tolerance verdicts. Maps to:
 *   - GET  /batches/{id}/workbook-comparison   (any role)
 *   - POST /batches/{id}/workbook-comparison   (reviewer/admin, X-Reason)
 * platform_value / workbook_value are decimal-as-string (§3).
 */

import { isApiError } from "@compliance/domain";
import {
  ActionIcon,
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Paper,
  ScrollArea,
  Skeleton,
  Stack,
  Table,
  Text,
  TextInput,
  Textarea,
  ThemeIcon,
  Tooltip,
} from "@mantine/core";
import { notifications } from "@mantine/notifications";
import { IconAlertCircle, IconPlus, IconTableOptions, IconTrash } from "@tabler/icons-react";
import { useQueryClient } from "@tanstack/react-query";
import { useState } from "react";
import { useRecordWorkbookComparison, useWorkbookComparison } from "../../api/hooks";
import type { BatchDetail, WorkbookCellInput } from "../../api/types";
import { useHasRole } from "../../auth";
import { queryKeys } from "../../lib/queryKeys";

type DraftCell = {
  cell_ref: string;
  metric: string;
  platform_value: string;
  workbook_value: string;
  agreed_variance: boolean;
  note: string;
};

const emptyCell = (): DraftCell => ({
  cell_ref: "",
  metric: "",
  platform_value: "",
  workbook_value: "",
  agreed_variance: false,
  note: "",
});

const DECIMAL_RE = /^-?\d+(\.\d+)?$/;

function cellComplete(c: DraftCell): boolean {
  return (
    c.cell_ref.trim() !== "" &&
    c.metric.trim() !== "" &&
    DECIMAL_RE.test(c.platform_value.trim()) &&
    DECIMAL_RE.test(c.workbook_value.trim())
  );
}

export function WorkbookComparisonPanel({ batch }: { batch: BatchDetail }) {
  const canRecord = useHasRole("reviewer", "admin");
  const listQ = useWorkbookComparison(batch.batch_id);
  const [open, setOpen] = useState(false);
  const variances = listQ.data ?? [];

  const outOfTolerance = variances.filter((v) => !v.within_tolerance && !v.agreed_variance).length;

  return (
    <Stack gap="sm">
      <Group justify="space-between" align="flex-end" wrap="wrap">
        <Stack gap={2}>
          <Group gap="sm">
            <ThemeIcon variant="light" color="grape" radius="md" size={30}>
              <IconTableOptions size={16} />
            </ThemeIcon>
            <Text fw={600}>Workbook comparison</Text>
            {outOfTolerance > 0 ? (
              <Badge color="red" variant="light" size="sm">
                {outOfTolerance} unexplained
              </Badge>
            ) : null}
          </Group>
          <Text size="xs" c="dimmed" maw={620}>
            Cell-level reconciliation against the legacy workbook during parallel run. Variance is
            within tolerance at ±0.01%.
          </Text>
        </Stack>
        {canRecord ? (
          <Button
            size="xs"
            variant="default"
            leftSection={<IconPlus size={14} />}
            onClick={() => setOpen(true)}
          >
            Record comparison
          </Button>
        ) : null}
      </Group>

      <Paper withBorder radius="md">
        {listQ.isPending ? (
          <Skeleton height={120} />
        ) : listQ.isError ? (
          // Distinguish a load failure from "no variances recorded" — otherwise a
          // transient/permission error hides failed reconciliation data.
          <Stack align="center" py="xl" gap="xs">
            <Text c="red" size="sm">
              {isApiError(listQ.error)
                ? `${listQ.error.code}: ${listQ.error.message}`
                : "Couldn't load the workbook comparison."}
            </Text>
          </Stack>
        ) : (
          <ScrollArea>
            <Table verticalSpacing="sm" horizontalSpacing="md" fz="sm" highlightOnHover miw={860}>
              <Table.Thead style={{ background: "var(--mantine-color-gray-0)" }}>
                <Table.Tr>
                  <Table.Th style={{ width: 90 }}>Cell</Table.Th>
                  <Table.Th>Metric</Table.Th>
                  <Table.Th ta="right">Platform</Table.Th>
                  <Table.Th ta="right">Workbook</Table.Th>
                  <Table.Th ta="right" style={{ width: 110 }}>
                    Variance %
                  </Table.Th>
                  <Table.Th style={{ width: 130 }}>Verdict</Table.Th>
                  <Table.Th>Note</Table.Th>
                </Table.Tr>
              </Table.Thead>
              <Table.Tbody>
                {variances.map((v) => (
                  <Table.Tr key={`${v.metric}-${v.cell_ref}`}>
                    <Table.Td ff="monospace">{v.cell_ref}</Table.Td>
                    <Table.Td>{v.metric}</Table.Td>
                    <Table.Td ta="right" ff="monospace" fz="xs">
                      {v.platform_value}
                    </Table.Td>
                    <Table.Td ta="right" ff="monospace" fz="xs">
                      {v.workbook_value}
                    </Table.Td>
                    <Table.Td ta="right" ff="monospace" fz="xs">
                      {v.variance_pct}
                    </Table.Td>
                    <Table.Td>
                      {v.within_tolerance ? (
                        <Badge color="teal" variant="light" size="sm">
                          in tolerance
                        </Badge>
                      ) : v.agreed_variance ? (
                        <Badge color="yellow" variant="light" size="sm">
                          agreed
                        </Badge>
                      ) : (
                        <Badge color="red" variant="light" size="sm">
                          variance
                        </Badge>
                      )}
                    </Table.Td>
                    <Table.Td c={v.note ? undefined : "dimmed"} fz="xs">
                      {v.note ?? "—"}
                    </Table.Td>
                  </Table.Tr>
                ))}
              </Table.Tbody>
            </Table>
          </ScrollArea>
        )}
        {!listQ.isPending && !listQ.isError && variances.length === 0 ? (
          <Stack align="center" py="xl" gap="xs">
            <Text c="dimmed" size="sm">
              No workbook variances recorded for this batch.
            </Text>
          </Stack>
        ) : null}
      </Paper>

      {canRecord ? (
        <RecordModal batch={batch} opened={open} onClose={() => setOpen(false)} />
      ) : null}
    </Stack>
  );
}

function RecordModal({
  batch,
  opened,
  onClose,
}: {
  batch: BatchDetail;
  opened: boolean;
  onClose: () => void;
}) {
  const record = useRecordWorkbookComparison();
  const queryClient = useQueryClient();
  const [cells, setCells] = useState<DraftCell[]>([emptyCell()]);
  const [reason, setReason] = useState("");

  const reasonValid = reason.trim().length >= 3;
  const completeCells = cells.filter(cellComplete);
  const canSubmit = completeCells.length > 0 && reasonValid && !record.isPending;

  const reset = () => {
    setCells([emptyCell()]);
    setReason("");
    record.reset();
  };

  const close = () => {
    if (record.isPending) return;
    reset();
    onClose();
  };

  const update = (i: number, patch: Partial<DraftCell>) => {
    setCells((prev) => prev.map((c, idx) => (idx === i ? { ...c, ...patch } : c)));
  };

  const onSubmit = async () => {
    const payloadCells: WorkbookCellInput[] = completeCells.map((c) => ({
      cell_ref: c.cell_ref.trim(),
      metric: c.metric.trim(),
      platform_value: c.platform_value.trim(),
      workbook_value: c.workbook_value.trim(),
      agreed_variance: c.agreed_variance,
      note: c.note.trim() || null,
    }));
    try {
      await record.mutateAsync({
        batchId: batch.batch_id,
        input: { cells: payloadCells },
        reason: reason.trim(),
      });
      notifications.show({
        title: "Comparison recorded",
        message: `${payloadCells.length} cell${payloadCells.length === 1 ? "" : "s"} compared.`,
        color: "teal",
        autoClose: 3500,
      });
      await queryClient.invalidateQueries({ queryKey: queryKeys.batches.workbook(batch.batch_id) });
      reset();
      onClose();
    } catch {
      // Surfaced inline below.
    }
  };

  return (
    <Modal opened={opened} onClose={close} title="Record workbook comparison" size="xl" centered>
      <Stack gap="md">
        <Text size="sm" c="dimmed">
          Enter the platform figure and the legacy-workbook figure per cell. The backend computes
          the variance and the ±0.01% tolerance verdict. Values are decimal strings.
        </Text>
        {record.error ? (
          <Alert color="red" variant="light" icon={<IconAlertCircle size={16} />}>
            <Text size="sm">
              {isApiError(record.error)
                ? `${record.error.code}: ${record.error.message}`
                : (record.error as Error).message}
            </Text>
          </Alert>
        ) : null}

        <ScrollArea.Autosize mah={360}>
          <Table fz="sm" verticalSpacing="xs">
            <Table.Thead>
              <Table.Tr>
                <Table.Th style={{ width: 110 }}>Cell ref</Table.Th>
                <Table.Th>Metric</Table.Th>
                <Table.Th style={{ width: 130 }}>Platform</Table.Th>
                <Table.Th style={{ width: 130 }}>Workbook</Table.Th>
                <Table.Th style={{ width: 80 }}>Agreed</Table.Th>
                <Table.Th>Note</Table.Th>
                <Table.Th style={{ width: 36 }} />
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {cells.map((c, i) => (
                // biome-ignore lint/suspicious/noArrayIndexKey: rows are positional draft inputs
                <Table.Tr key={i}>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="B12"
                      value={c.cell_ref}
                      onChange={(e) => update(i, { cell_ref: e.currentTarget.value })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="token_mmbtu"
                      value={c.metric}
                      onChange={(e) => update(i, { metric: e.currentTarget.value })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      inputMode="decimal"
                      placeholder="123.45"
                      value={c.platform_value}
                      error={
                        c.platform_value.trim() !== "" && !DECIMAL_RE.test(c.platform_value.trim())
                      }
                      onChange={(e) => update(i, { platform_value: e.currentTarget.value })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      inputMode="decimal"
                      placeholder="123.40"
                      value={c.workbook_value}
                      error={
                        c.workbook_value.trim() !== "" && !DECIMAL_RE.test(c.workbook_value.trim())
                      }
                      onChange={(e) => update(i, { workbook_value: e.currentTarget.value })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Group justify="center">
                      <Checkbox
                        checked={c.agreed_variance}
                        onChange={(e) => update(i, { agreed_variance: e.currentTarget.checked })}
                        aria-label="Agreed variance"
                      />
                    </Group>
                  </Table.Td>
                  <Table.Td>
                    <TextInput
                      size="xs"
                      placeholder="optional"
                      value={c.note}
                      onChange={(e) => update(i, { note: e.currentTarget.value })}
                    />
                  </Table.Td>
                  <Table.Td>
                    <Tooltip label="Remove row">
                      <ActionIcon
                        variant="subtle"
                        color="gray"
                        size="sm"
                        disabled={cells.length === 1}
                        onClick={() => setCells((prev) => prev.filter((_, idx) => idx !== i))}
                        aria-label="Remove row"
                      >
                        <IconTrash size={14} />
                      </ActionIcon>
                    </Tooltip>
                  </Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </ScrollArea.Autosize>

        <Group>
          <Button
            size="xs"
            variant="default"
            leftSection={<IconPlus size={14} />}
            onClick={() => setCells((prev) => [...prev, emptyCell()])}
          >
            Add row
          </Button>
          <Text size="xs" c="dimmed">
            {completeCells.length} of {cells.length} rows complete
          </Text>
        </Group>

        <Textarea
          label="Reason"
          description="Recorded in the audit log. Minimum 3 characters."
          minRows={2}
          autosize
          value={reason}
          onChange={(e) => setReason(e.currentTarget.value)}
          error={reason.length > 0 && !reasonValid ? "Reason must be at least 3 characters" : null}
        />

        <Group justify="flex-end">
          <Button variant="default" onClick={close} disabled={record.isPending}>
            Cancel
          </Button>
          <Button onClick={onSubmit} disabled={!canSubmit} loading={record.isPending}>
            Record {completeCells.length > 0 ? `· ${completeCells.length}` : ""}
          </Button>
        </Group>
      </Stack>
    </Modal>
  );
}
